import i18next, { type TOptionsBase } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createProxy, getPath, type ObjPathProxy } from "ts-object-path";

import type { AdvisoryManagementResourceDictionary } from "AdvisoryManagement/Translations/ResourceDictionary";
import type { CommissionDistributionResourceDictionary } from "CommissionDistribution/Translations/ResourceDictionary";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import type { TranslationResources } from "Infrastructure/Translations/TranslationResource";
import { logError } from "Infrastructure/Utils/LoggingUtils";

export const Resources = {
	default: createProxy<ResourceDictionary>(),
	advisoryManagement: createProxy<AdvisoryManagementResourceDictionary>(),
	commissionDistribution:
		createProxy<CommissionDistributionResourceDictionary>(),
};

export const GlobalResources = Resources.default;

export function useResource<T>(namespace?: string) {
	const { t: i18Translation } = useTranslation(namespace);

	const t = useMemo(() => {
		const translateF = <R extends string | string[] = string>(
			resourcePath: ObjPathProxy<T, string>,
			options?: any,
		) => {
			return i18Translation(
				getResourcePath(resourcePath),
				options,
			) as R extends string[] ? string[] : string;
		};
		return translateF;
	}, [i18Translation]);

	return {
		t,
	};
}

export function useServerError<T>(
	parentObject: ObjPathProxy<T, object>,
	fallbackProperty: ObjPathProxy<T, string>,
) {
	const { t } = useResource<T>();
	return {
		translateError: (code: string | null | undefined) => {
			if (!code) {
				return null;
			}

			const newCode = `${getResourcePath(parentObject as any)}.${code}`;
			const isCodeTranslated = i18next.exists(newCode);

			if (!isCodeTranslated) {
				console.warn(`Not found serverError translation for ${newCode}`);
				logError(
					new Error(`Not found serverError translation for ${newCode}`),
					null,
					false,
				);
			}

			const resource = isCodeTranslated
				? i18next.t(newCode)
				: t(fallbackProperty);

			return resource as string;
		},
	};
}

export function getTranslation<T>(
	resourcePath: ObjPathProxy<T, string>,
	ns?: TranslationResources,
	options?: (TOptionsBase & { [key: string]: unknown }) | undefined,
) {
	return i18next.t(
		`${ns ? `${ns}:` : ""}${getResourcePath(resourcePath)}`,
		options,
	);
}

export function getResourcePath<T>(proxy: ObjPathProxy<T, string>): string {
	const path = getPath(proxy);

	if (!path || path.length === 0) return "";
	if (path.length === 1) return path[0].toString();
	return `${path[0].toString()}.${path.slice(1).join(".")}`;
}
